import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components';
import { usePhases } from '../../contexts/PhaseContext';
import {
  PitchSubmissionDoc,
  emptyPitch,
  getPitch,
  savePitch,
} from '../../services/pitch-submission-service';
import { getMyStartup, StartupDoc } from '../../services/startup-service';
import PitchForm from './PitchForm';

type ReviewStatus = NonNullable<PitchSubmissionDoc['reviewStatus']>;

const OUTCOME_LABEL: Record<ReviewStatus, string> = {
  pending:              'Pending Review',
  revision_needed:      'Needs Revision',
  rejected:             'Rejected',
  approved:             'Approved',
  approved_distinction: 'Approved with Distinction',
};

const TONE_CLASSES: Record<ReviewStatus, string> = {
  pending:              'bg-chart/10 border-chart/20 text-chart',
  revision_needed:      'bg-warning/10 border-warning/20 text-warning',
  rejected:             'bg-danger/10 border-danger/20 text-danger',
  approved:             'bg-success/10 border-success/20 text-success',
  approved_distinction: 'bg-chart/10 border-chart/20 text-chart',
};

const GradeBanner: React.FC<{ pitch: PitchSubmissionDoc }> = ({ pitch }) => {
  const rs = pitch.reviewStatus;
  if (!rs) return null;

  if (rs === 'pending') {
    return (
      <div className="flex items-center gap-3 p-4 bg-chart/10 border border-chart/20 rounded-lg">
        <div className="w-2 h-2 rounded-full bg-chart animate-pulse flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-text">Pitch Under Review</p>
          <p className="text-xs text-muted mt-0.5">Your submission is being reviewed by a mentor. You'll be notified once graded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg border ${TONE_CLASSES[rs]}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider opacity-75">Mentor Review</span>
            {pitch.score != null && (
              <span className="font-display text-2xl tabular-nums leading-none">{pitch.score}/100</span>
            )}
          </div>
          <p className="font-semibold text-sm">{OUTCOME_LABEL[rs]}</p>
          {pitch.mentorFeedback && (
            <p className="text-xs mt-2 leading-relaxed opacity-90 whitespace-pre-wrap">{pitch.mentorFeedback}</p>
          )}
          {pitch.gradedAt && (
            <p className="text-xs mt-1.5 opacity-60">
              Graded {new Date(pitch.gradedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      </div>
      {(rs === 'revision_needed' || rs === 'rejected') && (
        <p className="text-xs mt-3 pt-3 border-t border-current/20 opacity-80">
          Address the feedback above and resubmit when ready.
        </p>
      )}
    </div>
  );
};

const FinalPitch: React.FC = () => {
  const { allPhasesComplete, loading: phaseLoading } = usePhases();
  const [pitch, setPitch] = useState<PitchSubmissionDoc>(emptyPitch('final'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [startup, setStartup] = useState<StartupDoc | null>(null);

  useEffect(() => {
    if (phaseLoading) return;
    if (!allPhasesComplete) { setLoading(false); return; }

    Promise.all([getPitch('initial'), getPitch('final'), getMyStartup()])
      .then(([ip, fp, s]) => {
        if (fp) {
          setPitch(fp);
        } else if (ip) {
          setPitch({ ...ip, type: 'final', _id: undefined, isSubmitted: false, submittedAt: null });
        }
        setStartup(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [allPhasesComplete, phaseLoading]);

  useEffect(() => {
    if (!startup || loading) return;
    setPitch(prev => ({
      ...prev,
      startupName:  prev.startupName  || startup.name             || '',
      website:      prev.website      || startup.website          || '',
      problem:      prev.problem      || startup.problemStatement || '',
      solution:     prev.solution     || startup.solution         || '',
      targetMarket: prev.targetMarket || startup.targetMarket     || '',
      industry:     prev.industry     || startup.industry         || '',
      revenueModel: prev.revenueModel || startup.revenueModel     || '',
      traction:     prev.traction     || startup.traction         || '',
    }));
  }, [startup, loading]);

  const rs = pitch.reviewStatus;
  const isReadOnly = rs === 'pending' || rs === 'approved' || rs === 'approved_distinction';
  const canSubmitForReview = !isReadOnly;

  const handleSave = async (data: PitchSubmissionDoc) => {
    setSaving(true);
    setError('');
    try {
      const saved = await savePitch('final', data);
      setPitch(saved);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    setError('');
    try {
      const saved = await savePitch('final', { ...pitch, isSubmitted: true });
      setPitch(saved);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (phaseLoading || loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!allPhasesComplete) {
    return (
      <div className="section-gap max-w-4xl">
        <Link
          to="/app/lms/courses"
          className="inline-flex items-center gap-1.5 text-muted hover:text-text text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back to Learning
        </Link>
        <div className="card border-warning/20 text-center py-12">
          <div className="w-12 h-12 rounded-full bg-warning/15 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-warning" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="heading text-xl">Final Pitch Locked</h2>
          <p className="text-muted mt-2 max-w-sm mx-auto">
            Complete all program phases to unlock your Final Pitch submission.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-gap max-w-4xl">
      <Link
        to="/app/lms/courses"
        className="inline-flex items-center gap-1.5 text-muted hover:text-text text-sm transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        Back to Learning
      </Link>

      <div>
        <span className="text-xs font-medium bg-chart/15 text-chart px-2.5 py-0.5 rounded-full mb-2 inline-block">Final Pitch</span>
        <h1 className="font-display text-4xl tracking-wide text-text leading-none">Final Pitch</h1>
        <p className="font-serif-display italic text-text text-lg mt-2">
          Your investor-ready final submission. Pre-filled from your initial pitch — update as needed.
        </p>
      </div>

      <GradeBanner pitch={pitch} />

      {error && (
        <p className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-4 py-3">{error}</p>
      )}

      <PitchForm
        type="final"
        data={pitch}
        readOnly={isReadOnly}
        onSaveDraft={handleSave}
        onSubmit={undefined}
        saving={saving}
      />

      {canSubmitForReview && (
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-line">
          <div>
            <p className="text-sm font-medium text-text">Ready to submit?</p>
            <p className="text-xs text-muted mt-0.5">
              Once submitted, your pitch will be sent to a mentor for scoring.
              {(pitch.submissionCount ?? 0) > 0 && ` Submission #${(pitch.submissionCount ?? 0) + 1}.`}
            </p>
          </div>
          <Button variant="primary" onClick={handleSubmitForReview} disabled={submitting}>
            {submitting ? 'Submitting…' : (pitch.submissionCount ?? 0) > 0 ? 'Resubmit for Review' : 'Submit for Review'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FinalPitch;
